import { useQuery } from '@tanstack/react-query';
import { useConfig } from '@lore/config';

const FOUR_HOURS = 4 * 60 * 60 * 1000;

/**
 * @typedef {Object} Network
 * @property {number} id
 * @property {string} name
 * @property {string} subdomain
 * @property {string} screeningUrl
 * @property {string} trainingUrl
 * @property {string} educatorBanner
 * @property {string} partnerBanner
 * @property {string} grants
 * @property {boolean} isActive
 * @property {string} logo
 * @property {string} city
 * @property {string} state
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {string} googleDriveUrl
 * @property {number} userSiteDistance
 * @property {number} closestSiteId
 * @property {Network} data - Self-referencing data property for component compatibility
 * @export
 */

/**
 * Custom hook to fetch nearby networks.
 * @returns {import('@tanstack/react-query').UseQueryResult & { data: Network[] }}
 */
export default function useNearbyNetworks() {
  const config = useConfig();
  const { apiRoot, headers } = config.connections.default;

  const query = useQuery({
    queryKey: ['networks', 'nearby'],
    queryFn: () => {
      const url = `${apiRoot}/core/networks/nearby?pageSize=4`;
      return fetch(url, {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch nearby networks');
        }
        return res.json();
      });
    },
    staleTime: FOUR_HOURS,
    cacheTime: FOUR_HOURS,
  });

  return {
    ...query,
    // Spread the original query properties and transform 'data'.r
    // If data exists, map over the nested 'data' array to ensure each item
    // contains a self-referencing 'data' property for component compatibility.
    // Returns an empty array if query.data is not yet available.
    data: query.data ? query.data.data.map((network) => ({
      ...network,
      data: network
    })) : []
  };
};
